import React from 'react'
import { withTranslation } from 'react-i18next'
import { useRouteMatch } from 'react-router-dom'
import { useHistory } from 'react-router'
import { isMobile } from 'react-device-detect'
import styled, { withTheme } from 'styled-components'
import PropTypes from 'prop-types'

import { COLORS } from 'COLORS'
import { TRANSITION_TIMING } from 'constants/sidebar'
import { LISTING_BUILDER_BASE } from 'constants/routes'
import { PERMISSION_KEYS } from 'constants/account/permissions'
import { Features } from 'constants/feature_names'
import { COBALT_APP_TYPE } from 'constants/themes'
import { SEGMENT_LOCATION } from 'constants/listing_builder/listing_builder'
import { JSPRO_BUCKET } from 'constants/s3'

import { hasAccess } from 'helpers/permissions'
import { sendCtaClickEvent } from 'helpers/segment'
import { useFeatureFlag } from 'hooks/feature_flag'
import useCobaltPricing from 'hooks/cobalt_pricing/cobalt_pricing'

import { BackButton } from 'ui_elements/BackButton/BackButton'
import { NavbarWrapper } from 'ui_elements/NavbarWrapper'
import { ImpersonationBar } from 'ui_elements/ImpersonationBar'
import { TokenUsageBar } from 'ui_elements/TokenUsageBar'

import { AlertsDropdown } from 'src/js/components/navbar/AlertsDropdown/AlertsDropdown'
import { AccountDropdown } from 'src/js/components/navbar/AccountDropdown/AccountDropdown'
import { AmazonAccountDropdown } from 'src/js/components/navbar/AmazonAccountDropdown/AmazonAccountDropdown'
import { GlobalSearch } from 'src/js/components/navbar/GlobalSearch/GlobalSearch'
import { ChatBotSlideOut } from 'src/js/components/chatbot/ChatBotSlideout'

import { GlobalSearch as CobaltGlobalSearch } from './GlobalSearch'

function getNavBarColour(props) {
  if (props.isFullScreenOverlay) {
    return COLORS.coolGrey100
  }

  return COLORS.white
}
const LOGO_URL = `${JSPRO_BUCKET}/images/js-icon-small.svg`

const StyledNavbarWrapper = styled(NavbarWrapper)`
  background-color: ${getNavBarColour};
  align-items: center;
  justify-content: ${props =>
    props.isListingBulder ? 'space-between' : 'flex-end'};
  ${props => props.isListingBulder && 'box-shadow: none; height: 49px'};
  padding-left: ${props => (props.locked ? '250px' : '52px')};
`
const NavbarRight = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  height: 100%;
  padding-right: 30px;
  justify-content: flex-end;
  background-color: transparent;
`
const Logo = styled.img`
  width: 40px;
  height: 35px;

  // Center the logo using absolute as we have a lot more of control
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`
const NavbarLink = styled.div`
  font-size: 13px;
  font-weight: 500;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.38;
  letter-spacing: normal;
  color: ${COLORS.grey900};
  transition: color 150ms ease;
  display: flex;
  align-items: center;
  [class^='DropdownWrapper'] {
    align-self: stretch;
    cursor: pointer;
  }
  a {
    color: ${COLORS.white};
  }
  &:hover,
  a:hover {
    color: ${COLORS.white};
  }
`
const SplitLine = styled.div`
  margin-right: 16px;
  width: 1px;
  height: 27px;
  background: #d4dde0;
`
const SearchWrapper = styled.div`
  display: 'flex';
  flex: 1;
  padding-left: ${props => (props.locked ? '280px' : '80px')};
  transition: padding-left 0.3s ${TRANSITION_TIMING};
  padding-top: 3;
`
const GlobalSearchWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex: 1;
`
const StyledBackButton = styled(BackButton)`
  height: 30px;
  margin: 0 0 0 ${props => (props.locked ? '276px' : '78px')};
`

export const NavbarComponent = ({
  isFullScreenOverlay,
  isSimpleNav,
  user,
  isImpersonating,
  appType,
  locked,
  t
}) => {
  const history = useHistory()

  const listingBuilderRoute = useRouteMatch(`${LISTING_BUILDER_BASE}/:id`)

  const renderImpersonationBar = () => {
    if (isImpersonating) {
      return <ImpersonationBar />
    }

    return null
  }

  const isCobalt = appType === COBALT_APP_TYPE
  const { isOnTokenPlan } = useCobaltPricing()

  const isOrangeGlobalSearchEnabled = useFeatureFlag(
    Features.ORANGE_GLOBAL_SEARCH
  )
  const isGlobalSearchBetaEnabled = useFeatureFlag(
    Features.COBALT_GLOBAL_SEARCH_BETA
  )
  const isMobileNavEnabled =
    useFeatureFlag(Features.MOBILE_NAV) && isMobile && !isCobalt

  const isChatBotEnabled = useFeatureFlag(Features.AI_CHATBOT)

  const renderAlerts = () => {
    const { permissions } = user

    if (!hasAccess(permissions, PERMISSION_KEYS.ALERTS)) {
      return null
    }

    return <AlertsDropdown />
  }

  const renderLeftNav = () => {
    if (listingBuilderRoute !== null) {
      const { params } = listingBuilderRoute

      return (
        <StyledBackButton
          locked={locked}
          linkText={t('generic.exitBuilder', 'Exit Builder')}
          onClick={() => {
            sendCtaClickEvent({
              text: 'Exit Builder',
              location: SEGMENT_LOCATION,
              extraProps: {
                listingId: params.id === 'new' ? null : params.id
              }
            })

            history.push(LISTING_BUILDER_BASE)
          }}
        />
      )
    }

    return null
  }

  const renderRightNav = () => {
    if (isSimpleNav) {
      return null
    }

    return (
      <NavbarRight>
        {isGlobalSearchBetaEnabled && isCobalt && (
          <SearchWrapper locked={locked}>
            <CobaltGlobalSearch />
          </SearchWrapper>
        )}
        {isOnTokenPlan && isCobalt && (
          <>
            <TokenUsageBar />
            <SplitLine />
          </>
        )}
        {isOrangeGlobalSearchEnabled && !isMobileNavEnabled && !isCobalt && (
          <GlobalSearchWrapper>
            <GlobalSearch />
          </GlobalSearchWrapper>
        )}
        {isChatBotEnabled && <ChatBotSlideOut />}
        {isMobileNavEnabled ? (
          <div
            style={{
              display: 'flex',
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            <Logo src={LOGO_URL} alt="Jungle Scout logo" />
          </div>
        ) : (
          <AmazonAccountDropdown />
        )}
        {!isFullScreenOverlay && <NavbarLink>{renderAlerts()}</NavbarLink>}
        <AccountDropdown />
      </NavbarRight>
    )
  }

  return (
    <StyledNavbarWrapper
      locked={locked}
      isSimpleNav={isSimpleNav}
      isFullScreenOverlay={isFullScreenOverlay}
      isListingBulder={listingBuilderRoute !== null}>
      {renderLeftNav()}
      {renderRightNav()}
      {renderImpersonationBar()}
    </StyledNavbarWrapper>
  )
}

NavbarComponent.defaultProps = {
  isImpersonating: false,
  isFullScreenOverlay: false,
  isSimpleNav: false,
  user: {}
}

NavbarComponent.propTypes = {
  isImpersonating: PropTypes.bool,
  isFullScreenOverlay: PropTypes.bool,
  isSimpleNav: PropTypes.bool,
  user: PropTypes.object
}

const wrappedNavbar = withTranslation('generic')(withTheme(NavbarComponent))

export { wrappedNavbar as Navbar }
